/**
 * Marketing-site target for "Get Started", login, and farm-preview links.
 *
 * Defaults to the legacy V1 host so the public site keeps working in any
 * environment that has not been reconfigured. For the V2 cutover, set
 * `REACT_APP_PLATFORM_BASE_URL` at build time to the V2 host
 * (e.g. `https://alpha.platform.farmtopia.farm` while alpha is the V2 home,
 * or the eventual production V2 host once promoted) so every CTA on the
 * marketing site lands new signups in the V2 platform.
 */
const DEFAULT_PLATFORM_BASE_URL = "https://beta.platform.farmtopia.farm";

export const getPlatformBaseUrl = () => {
  return (
    process.env.REACT_APP_PLATFORM_BASE_URL ||
    DEFAULT_PLATFORM_BASE_URL
  );
};

/**
 * Returns the base URL to use for local dev.
 * On Replit: uses the Replit dev domain (auto-detected via REACT_APP_REPLIT_DEV_DOMAIN).
 * On local dev: empty string → relative URLs.
 */
const getDevBaseUrl = () => {
  const replitDomain = process.env.REACT_APP_REPLIT_DEV_DOMAIN;
  if (replitDomain) {
    return `https://${replitDomain}`;
  }
  return "";
};

/**
 * Farm preview iframe URL.
 * Dev  → proxied through CRA server (/farms/* → platform:3001).
 * Prod → absolute production platform URL.
 */
export const buildPublicFarmPreviewUrl = (farmId) => {
  if (process.env.NODE_ENV === "development") {
    return `${getDevBaseUrl()}/farms/${farmId}/publicview`;
  }
  return `${getPlatformBaseUrl()}/farms/${farmId}/publicview`;
};

/**
 * Whether the embedded interactive farm preview iframe should render.
 *
 * The preview targets the V2 platform's `/farms/:id/publicview` route, which only
 * exists on V2. When the marketing build points at V1/beta (the default), that URL
 * 404s — so the iframe is OFF unless explicitly enabled. Set
 * REACT_APP_ENABLE_FARM_PREVIEW=1 (and REACT_APP_PLATFORM_BASE_URL to the live V2
 * host) once V2 is reachable. When off, the case study shows a graceful
 * "view on the platform" panel instead of a broken/404 embed.
 */
export const isFarmPreviewEnabled = () =>
  process.env.REACT_APP_ENABLE_FARM_PREVIEW === "1";

/**
 * Platform sign-up URL.
 * Dev  → proxied through CRA server (/sign-up → platform:3001/sign-up).
 * Prod → absolute production platform sign-up URL.
 */
export const buildPlatformSignUpUrl = () => {
  if (process.env.NODE_ENV === "development") {
    return `${getDevBaseUrl()}/sign-up`;
  }
  return `${getPlatformBaseUrl()}/sign-up`;
};

/**
 * Platform login URL.
 * Dev  → proxied through CRA server (/login → platform:3001/login).
 * Prod → absolute production platform URL.
 */
export const buildPlatformLoginUrl = () => {
  if (process.env.NODE_ENV === "development") {
    return `${getDevBaseUrl()}/login`;
  }
  return `${getPlatformBaseUrl()}/login`;
};

/**
 * Platform home URL.
 * Dev  → proxied through CRA server (/platform-home → platform:3001/).
 * Prod → absolute production platform URL.
 */
export const buildPlatformHomeUrl = () => {
  if (process.env.NODE_ENV === "development") {
    return `${getDevBaseUrl()}/platform-home`;
  }
  return getPlatformBaseUrl();
};
